import { ReactNode } from "react";
import { Link, useRoute } from "wouter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useCRMAuth } from "@/hooks/crm/useAuth";
import {
  LayoutDashboard,
  Users,
  GitBranch,
  Calendar,
  CheckSquare,
  FileText,
  LogOut,
  Settings,
  Mail,
  Send,
  UserCheck,
  BarChart3,
} from "lucide-react";

interface CRMLayoutProps {
  children: ReactNode;
}

const navigation = [
  {
    name: "Dashboard",
    href: "/crm/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Leads",
    href: "/crm/leads",
    icon: Users,
  },
  {
    name: "Pipeline",
    href: "/crm/pipeline",
    icon: GitBranch,
  },
  {
    name: "Kommunikation",
    href: "/crm/communications",
    icon: Mail,
  },
  {
    name: "Calendar",
    href: "/crm/calendar",
    icon: Calendar,
  },
  {
    name: "Tasks",
    href: "/crm/tasks",
    icon: CheckSquare,
  },
  {
    name: "Documents",
    href: "/crm/documents",
    icon: FileText,
  },
];

const newsletterNavigation = [
  {
    name: "Kampagnen",
    href: "/crm/newsletter/campaigns",
    icon: Send,
  },
  {
    name: "Abonnenten",
    href: "/crm/newsletter/subscribers",
    icon: UserCheck,
  },
];

export function CRMLayout({ children }: CRMLayoutProps) {
  const { user, logout } = useCRMAuth();

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 border-r bg-background flex flex-col shrink-0 overflow-hidden">
        {/* Logo/Brand */}
        <div className="p-6 border-b">
          <Link href="/crm/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-global-cyan rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">Y</span>
            </div>
            <div>
              <p className="font-semibold">YADE HOME</p>
              <p className="text-xs text-muted-foreground">CRM System</p>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {navigation.map((item) => {
              const [isActive] = useRoute(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-global-cyan text-white"
                      : "text-muted-foreground hover:bg-global-cyan hover:text-white"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <Separator className="my-4" />

          {/* Newsletter Section */}
          <div className="mb-4">
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Newsletter
            </h3>
            <nav className="space-y-1">
              {newsletterNavigation.map((item) => {
                const [isActive] = useRoute(item.href);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-global-cyan text-white"
                        : "text-muted-foreground hover:bg-global-cyan hover:text-white"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          <Separator className="my-4" />

          {/* Secondary Navigation */}
          <nav className="space-y-1">
            <Link
              href="/crm/settings"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-global-cyan hover:text-white transition-colors"
            >
              <Settings className="h-4 w-4" />
              Settings
            </Link>
          </nav>
        </ScrollArea>

        {/* User Profile & Logout */}
        <div className="border-t p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-global-cyan flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.username?.[0]?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.username || "User"}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email || ""}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => logout()}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-muted/10">
        <div className="max-w-7xl mx-auto p-6 min-h-0">
          {children}
        </div>
      </main>
    </div>
  );
}
